// Email typo suggester — evita erros comuns antes do envio.
// Baseado em distância Damerau-Levenshtein contra uma lista de domínios
// populares no Brasil + heurística para local-part suspeita.

const KNOWN_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "hotmail.com.br",
  "outlook.com",
  "outlook.com.br",
  "yahoo.com",
  "yahoo.com.br",
  "icloud.com",
  "live.com",
  "msn.com",
  "uol.com.br",
  "bol.com.br",
  "terra.com.br",
  "ig.com.br",
  "globo.com",
  "me.com",
  "protonmail.com",
];

const KNOWN_TLDS = ["com", "com.br", "org", "org.br", "net", "net.br", "edu.br"];

function damerauLevenshtein(a, b) {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  const d = Array.from({ length: al + 1 }, () => new Array(bl + 1).fill(0));
  for (let i = 0; i <= al; i++) d[i][0] = i;
  for (let j = 0; j <= bl; j++) d[0][j] = j;
  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[al][bl];
}

function suggestDomain(domain) {
  if (!domain) return null;
  const d = domain.toLowerCase().trim();
  if (KNOWN_DOMAINS.includes(d)) return null;
  let best = null;
  let bestDist = Infinity;
  for (const known of KNOWN_DOMAINS) {
    const dist = damerauLevenshtein(d, known);
    // Ceiling scales with length so we don't over-correct short domains.
    const maxDist = Math.max(1, Math.floor(known.length / 4));
    if (dist <= maxDist && dist < bestDist) {
      best = known;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Analyze an email and return either:
 *   { kind: "ok" }
 *   { kind: "invalid", reason: string }
 *   { kind: "typo", suggestion: string, reason: string }  // com correção acionável
 *   { kind: "warning", reason: string }                    // apenas alerta
 */
export function analyzeEmail(raw) {
  const email = (raw || "").trim();
  if (!email) return { kind: "ok" };

  // Basic format check
  const at = email.indexOf("@");
  if (at < 1 || at === email.length - 1) {
    return { kind: "invalid", reason: "Formato inválido — falta o @ ou o domínio" };
  }
  const local = email.slice(0, at).toLowerCase();
  const domain = email.slice(at + 1).toLowerCase();

  if (!/^[a-z0-9._%+-]+$/i.test(local)) {
    return { kind: "invalid", reason: "Caracteres inválidos no início do e-mail" };
  }
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)) {
    return { kind: "invalid", reason: "Domínio parece incompleto" };
  }

  // 1) Domain typo (highest priority — actionable suggestion)
  const suggestedDomain = suggestDomain(domain);
  if (suggestedDomain && suggestedDomain !== domain) {
    return {
      kind: "typo",
      suggestion: `${local}@${suggestedDomain}`,
      reason: `Você quis dizer ${suggestedDomain}?`,
    };
  }

  // 2) TLD typo (e.g. .con instead of .com)
  const tld = domain.split(".").slice(1).join(".");
  if (tld && !KNOWN_TLDS.includes(tld)) {
    // find closest known TLD
    let best = null;
    let bestDist = Infinity;
    for (const t of KNOWN_TLDS) {
      const dist = damerauLevenshtein(tld, t);
      if (dist <= 1 && dist < bestDist) {
        best = t;
        bestDist = dist;
      }
    }
    if (best) {
      const base = domain.split(".")[0];
      return {
        kind: "typo",
        suggestion: `${local}@${base}.${best}`,
        reason: `Você quis dizer ${base}.${best}?`,
      };
    }
  }

  // 3) Heuristic warnings (informational, no auto-fix)
  const noSeparator = !local.includes(".") && !local.includes("_") && !local.includes("+");
  if (local.length >= 15 && noSeparator) {
    return {
      kind: "warning",
      reason: "Endereço longo sem ponto — confira se digitou nome e sobrenome sem separador",
    };
  }

  return { kind: "ok" };
}
